import { useCart } from '@/lib/cart';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateItem, removeItem, clearAll, itemCount } = useCart();
  const { user, isPro } = useAuth();
  const navigate = useNavigate();

  if (!cartOpen) return null;

  const handleCheckout = () => {
    setCartOpen(false);
    navigate('/checkout');
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setCartOpen(false)} />

      {/* Drawer */}
      <div data-testid="cart-drawer" className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-black/10">
          <div className="flex items-center gap-2">
            <ShoppingBag size={20} className="text-[#00A5FF]" />
            <h2 className="text-lg font-bold">Panier ({itemCount})</h2>
          </div>
          <button onClick={() => setCartOpen(false)} data-testid="close-cart" className="p-1.5 hover:bg-[#F8F9FA] rounded-sm transition-colors duration-200">
            <X size={20} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {cart.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag size={48} className="text-black/15 mb-4" />
              <p className="text-sm text-black/50">Votre panier est vide</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.items.map((item) => (
                <div key={item.product_id} data-testid={`cart-item-${item.product_id}`} className="border border-black/10 p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 pr-2">
                      <p className="text-sm font-semibold leading-tight">{item.name}</p>
                      <p className="text-xs text-black/50 mt-0.5">SKU: {item.sku}</p>
                    </div>
                    <button onClick={() => removeItem(item.product_id)} className="p-1 text-black/30 hover:text-[#E8231B] transition-colors duration-200">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center border border-black/15">
                      <button
                        onClick={() => updateItem(item.product_id, Math.max(1, item.quantity - 1))}
                        className="p-1.5 hover:bg-[#F8F9FA] transition-colors duration-200"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="px-3 text-sm font-medium min-w-[32px] text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateItem(item.product_id, item.quantity + 1)}
                        className="p-1.5 hover:bg-[#F8F9FA] transition-colors duration-200"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <div className="text-right">
                      {isPro && item.pro_price !== item.public_price && (
                        <p className="text-xs text-black/40 line-through">{item.public_price.toFixed(2)} EUR</p>
                      )}
                      <p className="text-sm font-bold">{item.line_total.toFixed(2)} EUR</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.items.length > 0 && (
          <div className="border-t border-black/10 p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Total</span>
              <span className="text-lg font-bold">{cart.total.toFixed(2)} EUR</span>
            </div>
            <div className="text-xs text-black/50 space-y-1">
              <div className="flex justify-between">
                <span>Acompte commande (10%)</span>
                <span>{(cart.total * 0.1).toFixed(2)} EUR</span>
              </div>
              <div className="flex justify-between">
                <span>A la livraison (10%)</span>
                <span>{(cart.total * 0.1).toFixed(2)} EUR</span>
              </div>
              <div className="flex justify-between">
                <span>Solde à 30 jours (80%)</span>
                <span>{(cart.total * 0.8).toFixed(2)} EUR</span>
              </div>
            </div>
            {user?.status === 'validated' ? (
              <button
                onClick={handleCheckout}
                data-testid="checkout-button"
                className="w-full py-3 bg-[#00A5FF] text-white font-semibold text-sm rounded-sm hover:bg-[#0090e0] hover:-translate-y-0.5 transition-all duration-200"
              >
                Passer la commande
              </button>
            ) : (
              <p className="text-xs text-center text-[#E8231B]">
                Compte professionnel validé requis pour commander
              </p>
            )}
            <button
              onClick={clearAll}
              className="w-full py-2 text-sm text-black/50 hover:text-[#E8231B] transition-colors duration-200"
            >
              Vider le panier
            </button>
          </div>
        )}
      </div>
    </>
  );
}
